import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Heart, Brain, Users, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { heroContent } from '../mockData';
import { useServiceContent } from '../hooks/use-service-content';
import { useSectionsContent } from '../hooks/use-sections-content';
import { getServiceSlug } from '../lib/service-utils';
import { useLanguage } from '../i18n/LanguageContext';

const Home = () => {
  const { services } = useServiceContent();
  const { sectionsContent } = useSectionsContent();
  const { tr, localizeText } = useLanguage();

  const specialties = Array.isArray(sectionsContent?.specialties)
    ? sectionsContent.specialties
    : [];
  const testimonials = Array.isArray(sectionsContent?.testimonials)
    ? sectionsContent.testimonials
    : [];
  const activeServices = Array.isArray(services)
    ? services.filter((service) => service?.isActive !== false)
    : [];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroContent.image}
            alt={tr('home.hero.title')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sage-900/90 via-sage-800/75 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl">
            <Badge className="mb-6 bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30">
              {tr('home.badge')}
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {tr('home.hero.title')}
            </h1>
            <p className="text-xl text-sage-100 mb-4 leading-relaxed">
              {tr('home.hero.subtitle')}
            </p>
            <p className="text-lg text-sage-200 mb-8 leading-relaxed">
              {tr('home.hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/booking">
                <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white shadow-xl hover:shadow-2xl transition-all duration-300 text-lg px-8 group">
                  {tr('home.hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 hover:border-white/50 text-lg px-8">
                  {tr('action.learnMore')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-sage-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-sage-900 mb-4">{tr('home.expertise.title')}</h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              {tr('home.expertise.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specialties.map((specialty, index) => (
              <Card key={`${localizeText(specialty.title)}-${index}`} className="border-sage-200 hover:border-sage-400 transition-all duration-300 hover:shadow-lg bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-sage-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-sage-900 mb-2">{localizeText(specialty.title)}</h3>
                  <p className="text-sage-600">{localizeText(specialty.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-sage-900 mb-4">{tr('home.services.title')}</h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              {tr('home.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {activeServices.map((service) => {
              const IconComponent = service.icon === 'Brain' ? Brain : service.icon === 'Heart' ? Heart : Users;
              return (
                <Card key={service.id} className="overflow-hidden border-sage-200 hover:border-sage-400 transition-all duration-300 hover:shadow-xl group">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={localizeText(service.title)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-900/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2">
                        <IconComponent className="w-6 h-6 text-sage-600" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-semibold text-sage-900 mb-3">{localizeText(service.title)}</h3>
                    <p className="text-sage-600 mb-4 leading-relaxed">{localizeText(service.shortDescription)}</p>
                    <ul className="space-y-2 mb-6">
                      {(Array.isArray(service.benefits) ? service.benefits : []).slice(0, 3).map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-sage-700">
                          <CheckCircle className="w-4 h-4 text-sage-500 mt-0.5 flex-shrink-0" />
                          <span>{localizeText(benefit)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Link to={`/services/${getServiceSlug(service, localizeText)}`}>
                        <Button variant="outline" className="w-full border-sage-300 text-sage-700 hover:bg-sage-50 hover:border-sage-500 group">
                          {tr('action.learnMore')}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <Link to={`/booking?service=${encodeURIComponent(getServiceSlug(service, localizeText))}`}>
                        <Button className="w-full bg-sage-600 hover:bg-sage-700 text-white group">
                          {tr('services.bookThisService', 'Book This Service')}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-sage-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-sage-900 mb-4">{tr('home.experiences.title')}</h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              {tr('home.experiences.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id || index} className="border-sage-200 bg-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(Number(testimonial.rating) || 0)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-sage-700 mb-4 italic leading-relaxed">"{localizeText(testimonial.feedback)}"</p>
                  <p className="font-semibold text-sage-900">— {localizeText(testimonial.name)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-sage-600 to-earth-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">{tr('home.cta.title')}</h2>
          <p className="text-xl text-sage-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            {tr('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button size="lg" className="bg-white text-sage-700 hover:bg-sage-50 shadow-xl hover:shadow-2xl transition-all duration-300 text-lg px-8 group">
                {tr('action.bookSession')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                {tr('home.cta.contactUs')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
